import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Order, OrderWithContext, OrderStatus, Restaurant } from '@/models';
import { Routes } from '@/router';
import { HeaderFlags, FooterMode } from '@/store/ui';
import { getCountryByHostname, makeFormField } from '@/helpers';
import { GroupOrderErrorPageType } from '../GroupOrderError/cmg-group-order-error';
import { hasMaxGroupOrderParticipantMeals } from '@/services/order/order-helper';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { AnalyticsEventAction, AnalyticsEventCategory, AnalyticsEventLabel } from '@/services/google/google-analytics.services';

@Component({
  name: 'CmgGroupOrderJoin',
  beforeRouteLeave(to, from, next) {
    this.$services.ui.popHeaderState();
    this.$services.ui.popFooterState();
    this.$services.order.setIsGroupOrderParticipant(false);
    next();
  },
})
export default class CmgGroupOrderJoin extends Vue {
  readonly maxParticipantNameLength = 20;
  isLoading = true;
  order: Order | null = null;
  orderWithContext: OrderWithContext | null = null;
  participantName = makeFormField();
  getOrderRestaurantPromise: Promise<Restaurant> | null = null;

  get dancingVeggiesUrl() {
    return this.$appStore.state.cms.images.dancingVeggies.default;
  }

  async mounted() {
    this.$services.order.setIsGroupOrderParticipant(true);
    this.$services.ui.pushHeaderState({ flags: HeaderFlags.LogoVisibleXS });
    this.$services.ui.pushFooterState({ mode: FooterMode.Micro });

    if (!this.$route.params.groupOrderId) {
      this.redirectToNotFound();
      return;
    }

    try {
      // Odd case because of Windsor move to Order v3. We need to make sure the country profile
      // is set before grabbing the associated group order on mount, since the URL is country dependent
      const countryProfile = getCountryByHostname(window.location.hostname);
      if (countryProfile) {
        this.$services.ui.setCountryProfile(countryProfile, false);
      }

      this.orderWithContext = await this.$services.order.getGroupOrder(this.$route.params.groupOrderId);
      if (!this.orderWithContext || this.orderWithContext.order.orderStatus == OrderStatus.Submitted) {
        this.redirectToNotFound();
        return;
      }

      this.order = this.orderWithContext.order;

      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.Behavior,
        action: AnalyticsEventAction.Click,
        label: AnalyticsEventLabel.ViewGroupOrder,
        values: { customEventGroupOrderId: this.order.groupOrderId },
      });

      if (this.order.meals && hasMaxGroupOrderParticipantMeals(this.order.meals)) {
        this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.MaxParticipantCountReached } });
        return;
      }

      this.getOrderRestaurantPromise = this.$services.restaurant.getRestaurantByNumber(this.order.restaurantId);
      this.participantName = makeFormField(this.$appStore.state.order.groupOrderParticpantName || '');

      if (this.$services.order.isCustomerHostOfGroupOrder()) {
        this.popModalForGroupOrderAlreadyInProgress(this.$appStore.state.order.pendingGroupOrder!.order, this.orderWithContext.order);
      }
    } catch (e: any) {
      // TODO-84fwdq: Handle network error
      this.$services.logger.error('network error', e);
    } finally {
      this.isLoading = false;
    }
  }

  validateParticipantName() {
    if (this.participantName.value.length == 0) {
      this.participantName.error = 'Name Is Required';
    } else {
      this.participantName.error = null;
    }
  }

  onClickJoinGroup(): void {
    this.validateParticipantName();
    
    if (this.participantName.error || !this.getOrderRestaurantPromise) {
      return;
    }

    this.isLoading = true;
    this.$services.order.setPendingGroupOrder(this.orderWithContext, this.participantName.value);
    this.$services.order.clearGroupParticipantMeal();
    this.$services.order.setIsCustomerHostOfGroupOrder(false);

    try {
      this.getOrderRestaurantPromise.then(async restaurant => {
        this.$services.restaurant.setPreGroupOrderFlowRestaurant(this.$appStore.state.restaurant.selectedRestaurant);
        this.$services.restaurant.setGroupOrderRestaurant(restaurant);
        this.$services.restaurant.selectRestaurant(restaurant, this.$services.order.isPickingUp());
        localStorage.setItem("groupName", this.participantName.value);
        this.$router.push({ name: Routes.GroupOrderMenu });
      });
    } finally {
      this.isLoading = false;
    }
  }

  formatGroupOrderMessage(message: string) {
    return message;
  }

  popModalForGroupOrderAlreadyInProgress(orderToCancel: Order, orderTryingToJoin: Order) {
    this.$services.ui.setModalVisibility('basicError', true, {
      header: `Join ${orderTryingToJoin.customerFirstName}'s order?`,
      background: 'white',
      isShort: true,
      content: `You already have a Group Order in progress. You can join this one, but you'll lose the items in your bag if you do.`,
      onClose: () => {
        this.redirectToHome();
      },
      options: [
        {
          text: 'Cancel',
          type: ButtonType.Secondary,
          onClick: () => {
            this.redirectToHome();
          },
        },
        {
          text: 'Join Group Order',
          type: ButtonType.Primary,
          onClick: () => {
            this.$services.ui.setModalVisibility('basicError', false);
            this.$services.order.cancelOrder(orderToCancel.orderId, orderToCancel.orderType);
            this.$services.order.setPendingGroupOrder(null, '');
          },
        },
      ],
    });
  }

  private redirectToNotFound() {
    this.isLoading = false;
    this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
  }

  private redirectToHome() {
    this.$router.push({ name: Routes.Home });
    this.$services.ui.setModalVisibility('basicError', false);
    this.$services.bag.setBagVisibility(true);
  }
}
